import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { createSelector } from 'reselect';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { setLicenseKey } from 'survey-core';
import moment from 'moment/moment';
import { isAuthorized } from '../../components/AuthorizedAction.js';
import ChangeInsightsDetails from '../../components/change-insights/ChangeInsightsDetails.jsx';
import CIStudioFilters from '../../components/change-insights/ChangeInsightsFilters/index.jsx';
import ChangeInsightsHeader from '../../components/change-insights/ChangeInsightsHeader.jsx';
import EmptyChangeInsightsView from '../../components/change-insights/EmptyChangeInsightsView.jsx';
import SurveyReadySendBlock from '../../components/change-insights/SurveyReadySendBlock.jsx';
import Empty from '../../components/Empty.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import InitiativeHeader from '../../components/headers/PageHeaders/Initiative/InitiativeHeader.jsx';
import { ConfirmModal } from '../../components/modals/ConfirmModal.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  selectCurrentOrganizationId,
  selectUserPermissions,
} from '../../modules/auth.js';
import {
  deleteSurveyFromInitiative,
  onShareLink,
  retrieveChangeInsight,
  retrieveChangeInsightDetails,
  retrieveInitiativeRelations,
  retrieveTemplatesFeedbackForm,
  selectChangeInsightEmployeesResults,
  selectChangeInsightGroupsResults,
  selectChangeInsightOrganizationStakeholdersResults,
  selectChangeInsightParticipationsResults,
  selectChangeInsightSettings,
  selectChangeInsightStakeholdersResults,
  selectInitiativeStakeholder,
  selectRetrieveChangInsightsState,
  selectSurveyResponsesResults,
  selectTemplates,
  sendChangeInsightSurvey,
  updateChangeInsightDetails,
} from '../../modules/changeInsights.js';
import { selectInitiativeParticipation } from '../../modules/initiativeParticipations.js';
import {
  retrieveInitiative,
  retrieveInitiativeAncestry,
  retrieveInitiativeParticipations,
  searchInitiativeParticipations,
  selectAncestryInitiativeParticipations,
  selectInitiative,
  selectRetrieveInitiativeState,
  selectSearchQuery,
  updateSearchQuery,
} from '../../modules/initiatives.js';
import {
  retrieveOrganization,
  selectOrganization,
  selectRetrieveOrganizationState,
} from '../../modules/organizations.js';
import { selectProject } from '../../modules/projects.js';
import {
  retrieveInitiativeStakeholderParticipations,
  searchInitiativeStakeholderParticipations,
  selectInitiativeStakeholders,
} from '../../modules/stakeholderParticipations.js';
import ToggleBlock from '../../components/ToggleBlock.jsx';
import { resetFilters } from '../../components/change-insights/utils/index.js';
import { updateNotificationState } from '../../modules/notifications.js';
import ChangeInsightsReport from '../../components/change-insights/ChangeInsightsReport/ChangeInsightsReport.jsx';

const mapStateToProps = (
  state,
  { projectId, initiativeId, initiativeType, query }
) =>
  createSelector(
    selectInitiative(initiativeId, initiativeType),
    selectAncestryInitiativeParticipations,
    selectOrganization,
    selectSearchQuery,
    selectInitiativeStakeholders,

    (
      initiative,
      ancestryInitiativeParticipations,
      organization,
      searchQuery,
      initiativeStakeholders
    ) => {
      const retrieveSuccess = [
        selectRetrieveOrganizationState,
        selectRetrieveInitiativeState,
      ].every((select) => select(state).success);

      const { pending } = selectRetrieveChangInsightsState(state);

      // Derive participation IDs from query
      const initiativeParticipationId = query?.participant?.[0] || null;
      const stakeholderParticipationId = query?.stakeholder?.[0] || null;

      return {
        retrieveSuccess,
        pending,
        initiativeParticipationId,
        stakeholderParticipationId,
        initiative,
        organization,
        initiativeStakeholders,
        initiativeParticipation: selectInitiativeParticipation(
          initiativeParticipationId
        )(state),
        initiativeStakeholderParticipation: selectInitiativeStakeholder(
          stakeholderParticipationId
        )(state),
        ancestryInitiativeParticipations,
        organizationId: selectCurrentOrganizationId(state),
        templates: selectTemplates(state),
        project: selectProject(projectId),
        initiativeParticipations:
          selectChangeInsightParticipationsResults(state),
        changeInsightSettings: selectChangeInsightSettings(
          initiative ? initiative.surveyId : null
        )(state),
        surveyResponses: selectSurveyResponsesResults(state),
        employees: selectChangeInsightEmployeesResults(state),
        groups: selectChangeInsightGroupsResults(state),
        stakeholders: selectChangeInsightStakeholdersResults(state),
        organizationStakeholders:
          selectChangeInsightOrganizationStakeholdersResults(state),
        permissions: selectUserPermissions(state),
        apiUrl: state.config.apiUrl,
        surveyjsKey: state.config.surveyjsKey,
      };
    }
  )(state);

const mapDispatchToProps = (dispatch, { initiativeId, initiativeType }) =>
  bindActionCreators(
    {
      retrieveInitiative: () =>
        retrieveInitiative(initiativeType, initiativeId),
      retrieveInitiativeAncestry: () =>
        retrieveInitiativeAncestry(initiativeType, initiativeId),
      retrieveInitiativeParticipations: (initiativeParticipationId) =>
        retrieveInitiativeParticipations(
          initiativeType,
          initiativeId,
          initiativeParticipationId
        ),
      searchInitiativeParticipations: (query) =>
        searchInitiativeParticipations(initiativeType, initiativeId, query),
      retrieveInitiativeStakeholderParticipations: () =>
        retrieveInitiativeStakeholderParticipations(
          initiativeType,
          initiativeId
        ),
      searchInitiativeStakeholderParticipations: (query) =>
        searchInitiativeStakeholderParticipations(
          initiativeType,
          initiativeId,
          query
        ),
      updateSearchQuery,
      deleteSurveyFromInitiative,
      sendChangeInsightSurvey,
      retrieveChangeInsight,
      retrieveOrganization,
      retrieveChangeInsightDetails,
      retrieveInitiativeRelations,
      retrieveTemplatesFeedbackForm,
      updateChangeInsightDetails,
      onShareLink,
      updateNotificationState,
    },
    dispatch
  );

const ChangeInsightsDetailsContainer = (props) => {
  const {
    pending,
    retrieveSuccess,
    organizationId,
    initiativeId,
    organization,
    initiative,
    initiativeType,
    initiativeParticipationId,
    stakeholderParticipationId,
    surveyResponses,
    changeInsightSettings,
    templates,
    permissions,
    query,
    surveyjsKey,
    retrieveOrganization,
    retrieveChangeInsightDetails,
    retrieveInitiative,
    retrieveInitiativeRelations,
    retrieveTemplatesFeedbackForm,
    retrieveInitiativeStakeholderParticipations,
    retrieveInitiativeAncestry,
    onShareLink,
    sendChangeInsightSurvey,
    deleteSurveyFromInitiative,
    updateChangeInsightDetails,
    updateNotificationState,
  } = props;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [changeInsightId, setChangeInsightId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState({
    groupSearch: '',
    stakeholderSearch: '',
  });

  setLicenseKey(surveyjsKey);

  const isDateAfter = (date) =>
    moment(moment(new Date()).format('YYYY-MM-DD')).isAfter(moment(date));

  const notify = () => {
    updateNotificationState({
      isShown: true,
      type: 'success',
      forPath: '/change-insights/studio/show',
      message:
        'Feedback form link copied! Share it with your employees to start gathering feedback for the initiative.',
    });
  };

  const clearNotification = () => {
    setTimeout(
      () =>
        updateNotificationState({
          isShown: false,
          type: '',
          forPath: '',
          message: '',
        }),
      4000
    );
  };

  const handleClickSendSurvey = (emailSettings) => {
    if (changeInsightSettings) {
      setShowConfirmModal({
        show: true,
        id: changeInsightSettings.id,
        emailSettings,
      });
    }
  };

  const handleConfirmSendSurvey = (emailSettings) => {
    setLoading(true);
    sendChangeInsightSurvey(changeInsightId, { emailSettings })
      .then(() => {
        retrieveInitiative();
        retrieveChangeInsightDetails(changeInsightId);
      })
      .catch((e) => {
        setError({ field: 'sendSurvey', message: e.message });
      })
      .finally(() => {
        setShowConfirmModal({ show: false, id: null });
        setLoading(false);
      });
  };

  const handleUpdateEmailSetting = (emailSettings) => {
    setLoading(true);
    updateChangeInsightDetails(changeInsightId, emailSettings)
      .then(() => {
        retrieveInitiative();
        retrieveChangeInsightDetails(changeInsightId);
      })
      .catch((e) => {
        setError({ field: 'sendSurvey', message: e.message });
      })
      .finally(() => {
        setShowConfirmModal({ show: false, id: null });
        setLoading(false);
      });
  };

  const handleDeleteSurvey = (surveyId) => {
    setLoading(true);
    deleteSurveyFromInitiative(surveyId)
      .then(() => {
        retrieveInitiative();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleShowAddFeedbackForm = () => {
    if (initiative?.project.archived) {
      window.alert(getOrgTermsContent('archivedProjectMessage'));
    } else {
      setShowModal(true);
    }
  };

  const handlerResetSearch = (type) => {
    const queryValue = query || {};
    setSearch((prev) => ({ ...prev, [type]: '' }));

    retrieveChangeInsightDetails(initiative.surveyId, {
      ...Object.fromEntries(
        Object.entries(queryValue).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ''
        )
      ),
      [type]: '',
    });
  };

  let abortController = new AbortController();
  let { signal } = abortController;

  const handleSearchWithDelay = debounce((searchValue, type) => {
    const queryValue = query || {};

    abortController.abort();
    abortController = new AbortController();
    signal = abortController.signal;

    retrieveChangeInsightDetails(
      initiative.surveyId,
      {
        ...Object.fromEntries(
          Object.entries(queryValue).filter(
            ([_, value]) =>
              value !== undefined && value !== null && value !== ''
          )
        ),
        [type]: searchValue,
      },
      signal
    );
  }, 750);

  const handleClickCopyLink = () => {
    const { shareLink, newRecipients, sentAt } = changeInsightSettings;
    onShareLink(changeInsightId).then(() => {
      navigator.clipboard.writeText(shareLink);
      notify();
      clearNotification();
      if (newRecipients || !sentAt) {
        retrieveInitiative();
        retrieveChangeInsightDetails(changeInsightId);
      }
    });
  };

  const handleSearch = (searchValue, type) => {
    setSearch((prev) => ({ ...prev, [type]: searchValue }));
    handleSearchWithDelay(searchValue, type);
  };

  useEffect(() => {
    resetFilters(initiativeId, initiativeType);
    retrieveOrganization(organizationId);
    retrieveInitiativeStakeholderParticipations();
    retrieveInitiativeAncestry();

    retrieveInitiative().then(({ entities, result }) => {
      const entity = entities.initiatives[result];
      if (
        isAuthorized('update', `${entity.type}Initiative`, permissions) &&
        entity.project[
          `manage${entity.type.charAt(0).toUpperCase() + entity.type.slice(1)}Initiatives`
        ]
      ) {
        retrieveTemplatesFeedbackForm();
      }
    });
  }, []);

  useEffect(() => {
    if (initiative?.surveyId) {
      setChangeInsightId(initiative.surveyId);
    }
  }, [initiative]);

  useEffect(() => {
    if (changeInsightId) {
      const queryValue = query || {};

      // Transform to backend API param names
      const apiQuery = {};
      Object.entries(queryValue).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;

        if (key === 'participant' && Array.isArray(value) && value.length > 0) {
          apiQuery.initiative_participation_id = value[0];
        } else if (
          key === 'stakeholder' &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          apiQuery.stakeholder_participation_id = value[0];
        } else {
          apiQuery[key] = value;
        }
      });

      setSearch({ groupSearch: '', stakeholderSearch: '' });

      retrieveChangeInsightDetails(changeInsightId, apiQuery, true);
    }
  }, [query]);

  useEffect(() => {
    if (changeInsightId) {
      const queryValue = query || {};

      retrieveChangeInsightDetails(changeInsightId, {
        ...Object.fromEntries(
          Object.entries(queryValue).filter(
            ([_, value]) =>
              value !== undefined && value !== null && value !== ''
          )
        ),
      });
      retrieveInitiativeRelations(initiativeType, initiativeId);
    }
  }, [changeInsightId]);

  if (!retrieveSuccess) return <Empty simplified content={loadingContent} />;

  return (
    <React.Fragment>
      {initiative ? (
        <div
          className="change-insights-dashboard"
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            opacity: pending ? 0.5 : 1,
            cursor: pending ? 'wait' : 'auto',
            minHeight: 'calc(100vh - 366px)',
          }}
        >
          <InitiativeHeader initiative={initiative} hideSubHeader />
          <ChangeInsightsHeader
            {...props}
            loading={loading}
            survey={changeInsightSettings}
            isArchivedProject={initiative.project.archived}
            errors={error}
            handleDeleteSurvey={handleDeleteSurvey}
            handSendSurvey={handleConfirmSendSurvey}
            handleClickSendSurvey={handleClickSendSurvey}
            handleShowAddFeedbackForm={handleShowAddFeedbackForm}
            handleClickSendFeedbackForm={handleConfirmSendSurvey}
            handleUpdateEmailSetting={handleUpdateEmailSetting}
            handleClickCopyLink={handleClickCopyLink}
          />

          {pending && !changeInsightSettings ? (
            <Empty simplified content={loadingContent} />
          ) : null}

          {changeInsightSettings &&
          changeInsightSettings.newRecipients &&
          changeInsightSettings.sentAt ? (
            <SurveyReadySendBlock
              title={getOrgTermsContent('initiativeParticipantsAdded')}
              titleSettings={{ color: '#d2555a' }}
              description="Click the button below to send the feedback form to the new or updated participants."
              survey={changeInsightSettings}
              isArchivedProject={initiative.project.archived}
              isDateAfter={isDateAfter(changeInsightSettings.endDate)}
              handleClickSendFeedbackForm={handleConfirmSendSurvey}
              handleUpdateEmailSetting={handleUpdateEmailSetting}
              handleClickCopyLink={handleClickCopyLink}
              loading={loading}
              initiative={initiative}
              isCanManage={
                permissions && isAuthorized('update', 'survey', permissions)
              }
              initiativeType={initiative.type}
            />
          ) : null}

          {changeInsightSettings && !changeInsightSettings.sentAt ? (
            <SurveyReadySendBlock
              title="Feedback Form Is Ready to Send"
              description="Congratulations, your Feedback Form is set up and ready to be sent."
              survey={changeInsightSettings}
              isArchivedProject={initiative.project.archived}
              isDateAfter={isDateAfter(changeInsightSettings.endDate)}
              loading={loading}
              fullScreen
              initiative={initiative}
              handleClickSendFeedbackForm={handleConfirmSendSurvey}
              handleUpdateEmailSetting={handleUpdateEmailSetting}
              handleClickCopyLink={handleClickCopyLink}
              isCanManage={
                permissions && isAuthorized('update', 'survey', permissions)
              }
              initiativeType={initiative.type}
            />
          ) : null}

          {changeInsightSettings?.sentAt ? (
            <div style={{ pointerEvents: pending ? 'none' : 'auto' }}>
              <ToggleBlock title="Filters" showOnStart>
                <CIStudioFilters {...props} query={query} />
              </ToggleBlock>
            </div>
          ) : null}

          {changeInsightSettings?.sentAt ? (
            <ChangeInsightsReport
              dashboardSettings={
                changeInsightSettings.dashboardSettings
                  ? typeof changeInsightSettings.dashboardSettings === 'string'
                    ? JSON.parse(changeInsightSettings.dashboardSettings)
                    : changeInsightSettings.dashboardSettings
                  : []
              }
              changeInsightSettings={changeInsightSettings}
              surveyResponses={surveyResponses
                .map((r) => {
                  if (!r.surveyResult) return null;
                  return typeof r.surveyResult === 'string'
                    ? JSON.parse(r.surveyResult)
                    : r.surveyResult;
                })
                .filter(Boolean)}
              surveyJson={changeInsightSettings.surveyJson}
            />
          ) : null}

          {changeInsightSettings?.sentAt ? (
            pending ? (
              <Empty simplified content={loadingContent} />
            ) : (
              <ChangeInsightsDetails
                {...props}
                search={search}
                handleSearch={handleSearch}
                handleResetSearch={handlerResetSearch}
              />
            )
          ) : null}

          {!initiative.surveyId ? (
            <EmptyChangeInsightsView
              initiative={initiative}
              permissions={permissions}
              organization={organization}
              surveyTemplates={templates}
              showModal={showModal}
              handleShow={handleShowAddFeedbackForm}
              handleClose={() => setShowModal(false)}
            />
          ) : null}
          <ConfirmModal
            onConfirm={handleConfirmSendSurvey}
            onHide={() => setShowConfirmModal({ show: false, id: null })}
            show={showConfirmModal}
            loading={loading}
            message="Are you sure you want to send this feedback form?"
            title="Send Feedback Form"
          />
        </div>
      ) : null}
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeInsightsDetailsContainer);
